import { Archive, Inbox, Mail, Pencil, Plus, Search, Send, Trash2, UserPlus, UsersRound } from 'lucide-react'
import { useEffect, useMemo, useState, type FormEvent } from 'react'
import { api } from '../api/client'
import { Modal } from '../components/Modal'
import { PageTitle } from '../components/PageTitle'
import { SyncBadge } from '../components/StatusBadge'
import { useUIStore } from '../store/ui'
import type { AppMeta, Employee, Mailbox, MailboxKind } from '../types'

interface MailboxForm {
  name: string
  address: string
  postPublic: boolean
  allowSendAs: boolean
  archiveEnabled: boolean
  memberIds: number[]
}

export function GroupsPage() { return <MailboxesPage kind="group" /> }
export function PublicMailboxesPage() { return <MailboxesPage kind="public" /> }

function MailboxesPage({ kind }: { kind: MailboxKind }) {
  const isPublic = kind === 'public'
  const [items, setItems] = useState<Mailbox[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [meta, setMeta] = useState<AppMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Mailbox | null>(null)
  const [saving, setSaving] = useState(false)
  const [memberSearch, setMemberSearch] = useState('')
  const [form, setForm] = useState<MailboxForm>(emptyMailboxForm(isPublic))
  const notify = useUIStore((state) => state.notify)

  async function load() {
    setLoading(true)
    try {
      const [mailboxes, employeeData, appMeta] = await Promise.all([
        api<{ items: Mailbox[] }>(`/mailboxes?kind=${kind}`),
        api<{ items: Employee[] }>('/employees'),
        api<AppMeta>('/meta'),
      ])
      setItems(mailboxes.items || [])
      setEmployees((employeeData.items || []).filter((employee) => employee.status === 'active'))
      setMeta(appMeta)
    } catch (cause) {
      notify(cause instanceof Error ? cause.message : '加载失败', 'error')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void load() }, [kind]) // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return term ? items.filter((item) => item.name.toLowerCase().includes(term) || item.address.toLowerCase().includes(term)) : items
  }, [items, search])

  const visibleEmployees = useMemo(() => {
    const term = memberSearch.trim().toLowerCase()
    return term ? employees.filter((employee) => employee.name.toLowerCase().includes(term) || employee.email.toLowerCase().includes(term)) : employees
  }, [employees, memberSearch])

  function openCreate() {
    setEditing(null)
    setMemberSearch('')
    setForm({ ...emptyMailboxForm(isPublic), address: `@${meta?.domain || 'bluewhalewanders.net'}` })
    setFormOpen(true)
  }

  function openEdit(mailbox: Mailbox) {
    setEditing(mailbox)
    setMemberSearch('')
    setForm({ name: mailbox.name, address: mailbox.address, postPublic: mailbox.postPublic, allowSendAs: mailbox.allowSendAs, archiveEnabled: mailbox.archiveEnabled, memberIds: mailbox.memberIds || mailbox.members.map((member) => member.id) })
    setFormOpen(true)
  }

  async function save(event: FormEvent) {
    event.preventDefault()
    if (form.memberIds.length === 0) {
      notify('请至少选择一名成员', 'error')
      return
    }
    setSaving(true)
    const payload = { kind, ...form }
    try {
      if (editing) {
        await api<Mailbox>(`/mailboxes/${editing.id}`, { method: 'PATCH', body: JSON.stringify(payload) })
      } else {
        await api<Mailbox>('/mailboxes', { method: 'POST', body: JSON.stringify(payload) })
      }
      notify(`${isPublic ? '公共邮箱' : '群邮箱'}已${editing ? '更新' : '创建'}`)
      setFormOpen(false)
      await load()
    } catch (cause) {
      notify(cause instanceof Error ? cause.message : '保存失败', 'error')
    } finally {
      setSaving(false)
    }
  }

  async function remove(mailbox: Mailbox) {
    const archiveNote = mailbox.archiveEnabled ? '\n归档账户和历史邮件会保留，不会被删除。' : ''
    if (!window.confirm(`确定删除 ${mailbox.address}？删除后该地址将停止接收邮件。${archiveNote}`)) return
    try {
      await api(`/mailboxes/${mailbox.id}`, { method: 'DELETE' })
      notify(`${isPublic ? '公共邮箱' : '群邮箱'}已删除`)
      await load()
    } catch (cause) {
      notify(cause instanceof Error ? cause.message : '删除失败', 'error')
    }
  }

  function toggleMember(id: number) {
    setForm((current) => ({ ...current, memberIds: current.memberIds.includes(id) ? current.memberIds.filter((value) => value !== id) : [...current.memberIds, id] }))
  }

  return (
    <div className="page-stack">
      <PageTitle
        icon={isPublic ? <Inbox size={22} /> : <UsersRound size={22} />}
        title={isPublic ? '公共邮箱' : '群邮箱'}
        description={isPublic ? '多人共同接收邮件，并可使用统一的公共地址对外发信。' : '将发送到一个地址的邮件，自动分发给全部成员。'}
        action={<button className="button primary" onClick={openCreate}><Plus size={17} />新建{isPublic ? '公共邮箱' : '群邮箱'}</button>}
      />
      <div className={`feature-explainer ${isPublic ? 'teal' : 'violet'}`}>
        <div className="explainer-icon">{isPublic ? <Inbox size={24} /> : <Mail size={24} />}</div>
        <div><strong>{isPublic ? '公共邮箱如何工作？' : '群邮箱如何工作？'}</strong><p>{isPublic ? '例如 support@ 收到邮件后，每位成员都会在自己的收件箱看到一份；成员还可以使用 support@ 作为发件地址。开启归档后，系统会额外保留一份完整来信。' : '例如 sales@ 收到邮件后，会将同一封邮件分别投递给所有销售成员，不需要共享密码，也没有独立收件箱。'}</p></div>
      </div>
      <section className="panel table-panel">
        <div className="table-toolbar"><div className="search-box"><Search size={17} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={`搜索${isPublic ? '公共邮箱' : '群邮箱'}`} /></div><span className="record-count">共 {filtered.length} 个地址</span></div>
        <div className="mailbox-grid">
          {loading && <div className="empty-row">正在加载…</div>}
          {!loading && filtered.length === 0 && <div className="empty-card"><div>{isPublic ? <Inbox size={30} /> : <UsersRound size={30} />}</div><strong>还没有{isPublic ? '公共邮箱' : '群邮箱'}</strong><span>点击右上角按钮创建第一个地址</span></div>}
          {filtered.map((mailbox) => <article className="mailbox-card" key={mailbox.id}>
            <header><div className={`mailbox-symbol ${isPublic ? 'teal' : 'violet'}`}>{isPublic ? <Inbox size={21} /> : <UsersRound size={21} />}</div><div><strong>{mailbox.name}</strong><span>{mailbox.address}</span></div><SyncBadge status={mailbox.syncStatus} error={mailbox.syncError} /></header>
            <div className="mailbox-details">
              <div><span>成员</span><strong>{mailbox.members.length} 人</strong></div>
              <div><span>外部来信</span><strong>{mailbox.postPublic ? '允许' : '仅成员'}</strong></div>
              <div><span>成员代发</span><strong>{mailbox.allowSendAs ? '允许' : '关闭'}</strong></div>
              {isPublic && <div><span>归档副本</span><strong>{mailbox.archiveEnabled ? '已开启' : '未开启'}</strong></div>}
            </div>
            <div className="member-stack"><div className="avatar-stack">{mailbox.members.slice(0, 4).map((member) => <span key={member.id} title={member.name}>{member.name.slice(0, 1)}</span>)}{mailbox.members.length > 4 && <span>+{mailbox.members.length - 4}</span>}</div><p>{mailbox.members.slice(0, 3).map((member) => member.name).join('、')}{mailbox.members.length > 3 ? ' 等' : ''}</p></div>
            <footer><button className="button ghost compact" onClick={() => openEdit(mailbox)}><Pencil size={16} />编辑</button><button className="button danger-text compact" onClick={() => void remove(mailbox)}><Trash2 size={16} />删除</button></footer>
          </article>)}
        </div>
      </section>

      <Modal open={formOpen} title={`${editing ? '编辑' : '新建'}${isPublic ? '公共邮箱' : '群邮箱'}`} description={isPublic ? '设置公共地址、收件成员和代发权限。' : '设置群组地址和邮件接收成员。'} size="large" onClose={() => setFormOpen(false)} footer={<><button className="button ghost" onClick={() => setFormOpen(false)}>取消</button><button className="button primary" form="mailbox-form" disabled={saving}>{saving ? '正在保存…' : '保存设置'}</button></>}>
        <form id="mailbox-form" className="mailbox-form" onSubmit={save}>
          <div className="form-section"><h3>基本信息</h3><div className="form-grid"><label className="field"><span>显示名称</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={isPublic ? '例如：客户支持' : '例如：销售团队'} required autoFocus /></label><label className="field"><span>邮箱地址</span><input type="email" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value.toLowerCase() })} disabled={Boolean(editing)} required /><small>创建后地址不可修改</small></label></div></div>
          <div className="form-section"><h3>邮件规则</h3><div className="option-grid"><label className="check-card"><input type="checkbox" checked={form.postPublic} onChange={(e) => setForm({ ...form, postPublic: e.target.checked })} /><span className="check-box" /><div><strong><Mail size={17} />允许外部来信</strong><small>企业外部联系人也可以发送邮件到此地址</small></div></label><label className="check-card"><input type="checkbox" checked={form.allowSendAs} onChange={(e) => setForm({ ...form, allowSendAs: e.target.checked })} /><span className="check-box" /><div><strong><Send size={17} />允许成员代发</strong><small>成员可以使用该公共地址作为发件人</small></div></label>{isPublic && <label className="check-card"><input type="checkbox" checked={form.archiveEnabled} onChange={(e) => setForm({ ...form, archiveEnabled: e.target.checked })} /><span className="check-box" /><div><strong><Archive size={17} />保留归档副本</strong><small>将每封来信额外存入独立归档账户</small></div></label>}</div></div>
          <div className="form-section"><div className="section-heading"><div><h3>选择成员</h3><p>已选择 {form.memberIds.length} 人</p></div><div className="member-search"><Search size={16} /><input value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} placeholder="搜索员工" /></div></div><div className="member-picker">{visibleEmployees.length === 0 && <div className="empty-row">没有可选员工</div>}{visibleEmployees.map((employee) => <label className={`member-option ${form.memberIds.includes(employee.id) ? 'selected' : ''}`} key={employee.id}><input type="checkbox" checked={form.memberIds.includes(employee.id)} onChange={() => toggleMember(employee.id)} /><span className="person-avatar">{employee.name.slice(0, 1)}</span><div><strong>{employee.name}</strong><small>{employee.email}</small></div><span className="member-check">{form.memberIds.includes(employee.id) ? '✓' : <UserPlus size={16} />}</span></label>)}</div></div>
        </form>
      </Modal>
    </div>
  )
}

function emptyMailboxForm(isPublic: boolean): MailboxForm {
  return { name: '', address: '', postPublic: true, allowSendAs: isPublic, archiveEnabled: isPublic, memberIds: [] }
}
